import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL;
const BASE = `${API_URL}/api/reportjobs`;

export const GET_REPORT_JOBS = '@reportjob/list';
export const GET_REPORT_JOB = '@reportjob/get';
export const SET_REPORT_JOB = '@reportjob/set';
export const CLEAR_REPORT_JOB = '@reportjob/clear';
export const GET_REPORT_JOB_META = '@reportjob/meta';
export const SET_SUBJECT_LIST_PARSE = '@reportjob/parse-result';

/**
 * These actions return the axios promise as well as dispatching, so a caller
 * that needs to react to the outcome (advance the wizard, show a toast, keep a
 * spinner up) can await it. The reducer holds the shared state; the wizard
 * screens still need to know when a call finished and whether it worked.
 */

export function getReportJobMeta() {
  return async (dispatch) => {
    const response = await axios.get(`${BASE}/meta`);
    dispatch({ type: GET_REPORT_JOB_META, payload: response.data });
    return response.data;
  };
}

/** Short-lived URL for the subject-list Excel template shown on step 1. */
export function getSubjectListTemplateUrl() {
  return async () => {
    const response = await axios.get(`${BASE}/template/subject-list`);
    return response.data.url;
  };
}

export function getReportJobs(params = {}) {
  return async (dispatch) => {
    const response = await axios.post(`${BASE}/list`, params);
    dispatch({ type: GET_REPORT_JOBS, payload: response.data });
    return response.data;
  };
}

export function getReportJob(id) {
  return async (dispatch) => {
    const response = await axios.get(`${BASE}/${id}`);
    dispatch({ type: GET_REPORT_JOB, payload: response.data });
    return response.data;
  };
}

/**
 * Step 1. Creates the job and, when a file is given, uploads and parses the
 * subject list in the same request.
 */
export function createReportJobDraft(projectName, subjectListFile, { description, remarks } = {}) {
  return async (dispatch) => {
    const form = new FormData();
    form.append('projectName', projectName);
    if (description) form.append('description', description);
    if (remarks) form.append('remarks', remarks);
    if (subjectListFile) form.append('subjectList', subjectListFile);

    const response = await axios.post(`${BASE}/draft`, form);
    dispatch({ type: SET_REPORT_JOB, payload: response.data.job });
    if (response.data.parseResult) {
      dispatch({ type: SET_SUBJECT_LIST_PARSE, payload: response.data.parseResult });
    }
    return response.data;
  };
}

/** Upload a subject list. mode 'append' keeps manual subjects; 'replace' (server default) rebuilds the list. */
export function uploadSubjectList(jobId, subjectListFile, mode) {
  return async (dispatch) => {
    const form = new FormData();
    form.append('subjectList', subjectListFile);

    const response = await axios.post(`${BASE}/${jobId}/subject-list`, form, {
      params: mode ? { mode } : undefined
    });
    dispatch({ type: SET_REPORT_JOB, payload: response.data.job });
    dispatch({ type: SET_SUBJECT_LIST_PARSE, payload: response.data.parseResult });
    return response.data;
  };
}

export function updateReportJob(jobId, changes) {
  return async (dispatch) => {
    const response = await axios.put(`${BASE}/${jobId}`, changes);
    dispatch({ type: SET_REPORT_JOB, payload: response.data });
    return response.data;
  };
}

/**
 * Step 2. `kind` is 'resource' or 'site-visit'. Leaving subjectId out lets the
 * server suggest one per file; the response says where each suggestion came
 * from so the screen can label it honestly.
 */
export function uploadJobFiles(jobId, files, { kind = 'resource', subjectId, documentType } = {}) {
  return async (dispatch) => {
    const form = new FormData();
    Array.from(files).forEach((file) => form.append('files', file));
    form.append('kind', kind);
    if (subjectId) form.append('subjectId', subjectId);
    if (documentType) form.append('documentType', documentType);

    const response = await axios.post(`${BASE}/${jobId}/files`, form);
    dispatch({ type: SET_REPORT_JOB, payload: response.data.job });
    return response.data;
  };
}

/** Step 2, source comments. Each upload is its own batch; earlier ones stay. */
export function uploadSourceComments(jobId, files, { subjectIds = [], note = '' } = {}) {
  return async (dispatch) => {
    const form = new FormData();
    Array.from(files).forEach((file) => form.append('files', file));
    form.append('subjectIds', JSON.stringify(subjectIds));
    form.append('note', note);

    const response = await axios.post(`${BASE}/${jobId}/source-comments`, form);
    dispatch({ type: SET_REPORT_JOB, payload: response.data });
    return response.data;
  };
}

/**
 * Assigns a file: { subjectIds, documentType, sections }. At least one subject
 * and one section. (Older form: { subjectId, documentType }.)
 */
export function reassignJobFile(jobId, fileId, changes) {
  return async (dispatch) => {
    const response = await axios.put(`${BASE}/${jobId}/files/${fileId}`, changes);
    dispatch({ type: SET_REPORT_JOB, payload: response.data });
    return response.data;
  };
}

/** Not relevant to this project: the file stays in the list, greyed, and can be restored. */
export function excludeJobFile(jobId, fileId) {
  return async (dispatch) => {
    const response = await axios.post(`${BASE}/${jobId}/files/${fileId}/exclude`);
    dispatch({ type: SET_REPORT_JOB, payload: response.data });
    return response.data;
  };
}

/** Brings a not-relevant file back to the needs-assignment queue. */
export function restoreJobFile(jobId, fileId) {
  return async (dispatch) => {
    const response = await axios.post(`${BASE}/${jobId}/files/${fileId}/restore`);
    dispatch({ type: SET_REPORT_JOB, payload: response.data });
    return response.data;
  };
}

/** Admin: confidence needed before the matcher sorts a file on its own ('High' or 'Medium'). */
export function setMatchThreshold(jobId, matchThreshold) {
  return async (dispatch) => {
    const response = await axios.put(`${BASE}/${jobId}/match-threshold`, { matchThreshold });
    dispatch({ type: SET_REPORT_JOB, payload: response.data });
    return response.data;
  };
}

/** Accepts the proposed handling of one 'File checks before matching' item. */
export function acceptFileCheck(jobId, key) {
  return async (dispatch) => {
    const response = await axios.post(`${BASE}/${jobId}/file-checks/accept`, { key });
    dispatch({ type: SET_REPORT_JOB, payload: response.data });
    return response.data;
  };
}

export function deleteJobFile(jobId, fileId) {
  return async (dispatch) => {
    const response = await axios.delete(`${BASE}/${jobId}/files/${fileId}`);
    dispatch({ type: SET_REPORT_JOB, payload: response.data });
    return response.data;
  };
}

/**
 * Marks requirements as "Not Available At Present", excludes a subject, or
 * corrects a parsed row.
 */
export function updateJobSubject(jobId, subjectId, changes) {
  return async (dispatch) => {
    const response = await axios.put(`${BASE}/${jobId}/subjects/${subjectId}`, changes);
    // v6 may answer { subject } instead of the whole job; only a job replaces state.
    if (response.data && response.data._id) dispatch({ type: SET_REPORT_JOB, payload: response.data });
    return response.data;
  };
}

/** Step 3. Outstanding mandatory files do not prevent this. */
export function submitReportJob(jobId) {
  return async (dispatch) => {
    const response = await axios.post(`${BASE}/${jobId}/submit`);
    dispatch({ type: SET_REPORT_JOB, payload: response.data.job });
    return response.data;
  };
}

/** The "update source files" action on a finished job. */
export function regenerateReportJob(jobId, reason, templateId) {
  return async (dispatch) => {
    const response = await axios.post(`${BASE}/${jobId}/regenerate`, { reason, ...(templateId ? { templateId } : {}) });
    dispatch({ type: SET_REPORT_JOB, payload: response.data.job });
    return response.data;
  };
}

/** Returns a short-lived signed URL for one file on the job. */
export function getJobFileUrl(jobId, blobName) {
  return async () => {
    const response = await axios.post(`${BASE}/${jobId}/download`, { blobName });
    return response.data.url;
  };
}

export function deleteReportJob(jobId) {
  return async (dispatch) => {
    const response = await axios.delete(`${BASE}/${jobId}`);
    dispatch({ type: CLEAR_REPORT_JOB });
    return response.data;
  };
}

export function clearReportJob() {
  return (dispatch) => dispatch({ type: CLEAR_REPORT_JOB });
}

/* ------------------------------------------------------------------ */
/* v6 additions. Subject and DD calls return the response data only;   */
/* the callers (project and subject workspaces) keep their own state.   */
/* ------------------------------------------------------------------ */

/** Manual add. Rejects with 409 `duplicate` for the same name and type. */
export function addJobSubject(jobId, subject) {
  return async () => {
    const response = await axios.post(`${BASE}/${jobId}/subjects`, subject);
    return response.data;
  };
}

/** Soft delete; reason is at least 5 characters. */
export function deleteJobSubject(jobId, subjectId, reason) {
  return async () => {
    const response = await axios.delete(`${BASE}/${jobId}/subjects/${subjectId}`, { data: { reason } });
    return response.data;
  };
}

export function restoreJobSubject(jobId, subjectId) {
  return async () => {
    const response = await axios.post(`${BASE}/${jobId}/subjects/${subjectId}/restore`);
    return response.data;
  };
}

const dd = (jobId, subjectId) => `${BASE}/${jobId}/dd${subjectId ? `/subjects/${subjectId}` : ''}`;

export function getDdOverview(jobId) {
  return async () => (await axios.get(`${dd(jobId)}/overview`)).data;
}

/** Also triggers the one-off import of the scan result into stages and findings. */
export function getDdSubject(jobId, subjectId) {
  return async () => (await axios.get(dd(jobId, subjectId))).data;
}

export function getDdStage(jobId, subjectId, stage) {
  return async () => (await axios.get(`${dd(jobId, subjectId)}/stages/${stage}`)).data;
}

/** body: { action: 'markReviewed'|'markForReview'|'setNA'|'clearNA'|'rerun', remarks? } */
export function updateDdStage(jobId, subjectId, stage, body) {
  return async () => (await axios.put(`${dd(jobId, subjectId)}/stages/${stage}`, body)).data;
}

export function createDdFinding(jobId, subjectId, stage, body) {
  return async () => (await axios.post(`${dd(jobId, subjectId)}/stages/${stage}/findings`, body)).data;
}

export function updateDdFinding(jobId, subjectId, stage, findingId, body) {
  return async () => (await axios.put(`${dd(jobId, subjectId)}/stages/${stage}/findings/${findingId}`, body)).data;
}

export function decideDdFinding(jobId, subjectId, stage, findingId, decision, reason) {
  return async () =>
    (await axios.post(`${dd(jobId, subjectId)}/stages/${stage}/findings/${findingId}/decision`, { decision, reason }))
      .data;
}

export function deleteDdFinding(jobId, subjectId, stage, findingId, reason) {
  return async () =>
    (await axios.delete(`${dd(jobId, subjectId)}/stages/${stage}/findings/${findingId}`, { data: { reason } })).data;
}

export function restoreDdFinding(jobId, subjectId, stage, findingId) {
  return async () =>
    (await axios.post(`${dd(jobId, subjectId)}/stages/${stage}/findings/${findingId}/restore`)).data;
}

export function confirmDdFindingIdentity(jobId, subjectId, stage, findingId, reason) {
  return async () =>
    (await axios.post(`${dd(jobId, subjectId)}/stages/${stage}/findings/${findingId}/confirm-identity`, { reason }))
      .data;
}

export function includeDdFinding(jobId, subjectId, stage, findingId, reason) {
  return async () =>
    (await axios.post(`${dd(jobId, subjectId)}/stages/${stage}/findings/${findingId}/include`, { reason })).data;
}

/** Admin only. */
export function approveDdSubject(jobId, subjectId) {
  return async () => (await axios.post(`${dd(jobId, subjectId)}/approve`)).data;
}

/** Admin only. 409 `report_released` once the report is released. */
export function removeDdSubjectApproval(jobId, subjectId) {
  return async () => (await axios.delete(`${dd(jobId, subjectId)}/approve`)).data;
}

export function setDdRouting(jobId, subjectId, sourceKey, off, reason) {
  return async () => (await axios.put(`${dd(jobId, subjectId)}/routing/${sourceKey}`, { off, reason })).data;
}

export function getDdAudit(jobId, { subjectId, page = 1, pageSize = 20 } = {}) {
  return async () => (await axios.get(`${dd(jobId)}/audit`, { params: { subjectId, page, pageSize } })).data;
}

export function checkDdReport(jobId) {
  return async () => (await axios.post(`${dd(jobId)}/report-check`)).data;
}

/** Admin only; sets releasedAt. */
export function releaseDdReport(jobId) {
  return async () => (await axios.post(`${dd(jobId)}/release`)).data;
}

/* Report templates */

export function getReportTemplates() {
  return async () => (await axios.get(`${API_URL}/api/reporttemplates`)).data;
}

export function uploadReportTemplate(file, { name, notes }) {
  return async () => {
    const form = new FormData();
    form.append('file', file);
    form.append('name', name);
    if (notes) form.append('notes', notes);
    return (await axios.post(`${API_URL}/api/reporttemplates`, form)).data;
  };
}

/** Returns the response data; the API says where the file is (url). */
export function getReportTemplateDownload(templateId) {
  return async () => (await axios.get(`${API_URL}/api/reporttemplates/${templateId}/download`)).data;
}
